<template>
  <div
    class="user-info pointer relative"
    :class="{ active: memberMenuOpened, 'show-hand': activateOrganisation }"
    title="Toggle menu"
    @click="activateOrganisation ? toggleMenuDropdown() : null">
    <span
      :style="userAvatar ? `background-image: url(${userAvatar})` : ''"
      class="user-avatar round-wrap no-shrink show-hand"
      :class="{ active: memberMenuOpened }"
      @click="!activateOrganisation ? toggleMenuDropdown() : null" >
      {{ userAvatar ? '' : userName | initials }}
      <span
        :class="{green: available, red: !available}"
        class="indicator"/>
    </span>

    <div
      v-if="memberMenuOpened"
      v-click-outside="closeMenuDropdown"
      class="floating-menu">
      <section
        class="user-section"
        @click.stop >
        <span
          :style="userAvatar ? `background-image: url(${userAvatar})` : ''"
          class="avatar round-wrap no-shrink">
          {{ userAvatar ? '' : userName | initials }}
        </span>
        <div class="details">
          <div class="name ellipsis">{{ userName }}</div>
          <div class="email ellipsis">{{ userEmail }}</div>
        </div>
      </section>
      <section
        v-if="!activateOrganisation"
        class="availability-section flex center-all">
        <span class="status">Away mode</span>
        <SBIcon
          id="UserAvailabilityDescription"
          :icon="SBIconList.INFO"
          :color="SBColors.greyPrimary"
          :background-color="SBColors.white"
          :size="12"
          :hover-size="22"
          :border-radius="20" />
        <span class="greedy" />
        <SwitchCheckbox
          id="UserAvailabilitySwitch"
          :value="!available"
          class="dropdown-check"
          size="mini no-label-margin"
          outline-color="#ff5b61"
          switch-color="#ffffff"
          off-outline-color="#bac8ce"
          off-switch-color="#ffffff"
          @input="toggleAvailability"/>

        <b-popover
          target="UserAvailabilityDescription"
          triggers="hover"
          placement="top"
          :delay="{ show: 200, hide: 0 } " >
          Turn on away mode to inform your team members you are not available at the moment.
        </b-popover>
      </section>
      <section
        class="links-section">
        <router-link
          v-if="!basic && !activateOrganisation"
          class="item"
          :to="{ name: 'ProfileInfo' }"
          @click.native="closeMenuDropdown">
          <span class="custom-icon user-account" />
          <span class="title">Account</span>
        </router-link>
        <a
          class="item"
          @click="logout">
          <span class="custom-icon user-logout" />
          <span class="title">Log Out</span>
        </a>
      </section>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import SBIconList from '@/helpers/lists/sb-icon-list';
import SBColors from '@/helpers/lists/colors';
import SBIcon from '@/components/common/Icon';
import SwitchCheckbox from '@/components/common/SwitchCheckbox';

export default {
  name: 'SelectMember',
  components: {
    SBIcon,
    SwitchCheckbox,
  },
  props: {
    basic: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      memberMenuOpened: false,
      SBIconList,
      SBColors,
    };
  },
  computed: {
    ...mapGetters({
      userAvatar: 'user/avatar',
      userName: 'user/name',
      userEmail: 'user/email',
      available: 'organisation/myAvailability',
      currentOrganisation: 'organisation/currentOrganisation',
    }),
    activateOrganisation() {
      return this.currentOrganisation && (this.currentOrganisation.should_activate_account || this.currentOrganisation.should_reactivate_account);
    },
    organisationId() { return this.$route.params.organisationId; },
  },
  methods: {
    ...mapActions({
      logout: 'user/logout',
      setAvailability: 'organisation/setMyAvailability',
    }),
    closeMenuDropdown() {
      this.memberMenuOpened = false;
    },
    toggleAvailability() {
      this.setAvailability({ organisationId: this.organisationId, isAvailable: !this.available });
    },
    toggleMenuDropdown() {
      this.memberMenuOpened = !this.memberMenuOpened;
    },
    wrapperClick() {
      if (this.activateOrganisation) {
        this.toggleMenuDropdown();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.user-info {
  padding: 7px;
  border-radius: 50px;
  .user-avatar {
    position: relative;
    height: 20px;
    width: 20px;
    margin: 2px 2px;
    font-size: 12px;
    font-weight: 600;
    vertical-align: center;

    .indicator {
      display: inline-block;
      position: absolute;
      bottom: -2px;
      right: -2px;
      height: 8px;
      width: 8px;
      border-radius: 50%;
      border: white 1px solid;

      &.red {
        background-color: $redPrimary;
      }
      &.green {
        background-color: $greenPrimary;
      }
    }

    &:hover, &.active {
      filter: brightness(85%);
    }
  }

  &.show-hand .user-avatar {
    filter: none;
  }

  .floating-menu {
    overflow: hidden;
    position: absolute;
    left: 0;
    top: 36px;
    width: 299px;
    z-index: 999999;
    margin: 0;
    border-radius: 5px;
    box-shadow: 0 2px 5px 0 rgba(13, 15, 22, 0.23);
    background-color: $white;

    section {
      border-bottom: solid 1px $greyBorderSecondary;

      &:last-of-type {
        border-bottom: none;
      }
    }

    .user-section {
      padding: 12px;
      display: flex;
      justify-content: left;
      align-items: center;
      cursor: default !important;

      .avatar {
        height: 30px;
        width: 30px;
        border-radius: 15px;
        position: relative;
        margin: 0;
        font-size: 16px;
        font-weight: 600;
      }
      .details {
        margin-left: 8px;
        max-width: calc(100% - 38px);

        .name {
          font-size: 14px;
          font-weight: 500;
          font-stretch: normal;
          font-style: normal;
          line-height: 1.2;
          letter-spacing: normal;
          text-align: left;
          color: $blackPrimary;
        }
        .email {
          font-size: 12px;
          font-weight: normal;
          font-stretch: normal;
          font-style: normal;
          line-height: 1.4;
          letter-spacing: normal;
          text-align: left;
          color: $blackUltralight;
        }
      }
    }
    .availability-section {
      padding: 12px;

      .status {
        font-size: 14px;
        font-weight: 500;
        font-stretch: normal;
      }

      .description {
        padding-right: 24px;
        margin-top: 5px;
        font-size: 12px;
        line-height: 1.4;
        text-align: left;
        color: $greyPrimary;
      }
    }

    .links-section {
      display: flex;
      flex-direction: column;

      // reset router link styling
      a, a:hover {
        text-decoration: none;
        color: inherit;
      }

      .item {
        padding: 14px 12px;
        cursor: pointer;
        display: flex;
        align-items: center;

        &:last-of-type {
          border-radius: 0 0 5px 5px;
        }

        .custom-icon {
          height: 16px;
          width: 16px;
          display: inline-block;
          background-repeat: no-repeat;
          background-position: center;

          &.user-account {
            background-image: url('~@/assets/img/SB-Icons/profile-square.svg');
          }
          &.user-business {
            background-image: url('~@/assets/img/SB-Icons/user-business-light.svg');
          }
          &.user-logout {
            background-image: url('~@/assets/img/SB-Icons/logout.svg');
          }
        }
        .title {
          margin-left: 12px;
          margin-right: 12px;
          font-size: 14px;
        }


        &:hover {
          background-color: $hover;

          .custom-icon {
            // &.user-account {
            //   background-image: url('~@/assets/img/SB-Icons/user-account-dark.svg');
            // }
            // &.user-business {
            //   background-image: url('~@/assets/img/SB-Icons/user-business-dark.svg');
            // }
            // &.user-logout {
            //   background-image: url('~@/assets/img/SB-Icons/user-logout-dark.svg');
            // }
          }
        }
      }
    }
  }
}
</style>
